import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogIn, UserPlus } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useAuth } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-[#0a0a0a] border-zinc-800 text-white h-11 rounded-sm placeholder:text-zinc-600";

const formatDetail = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

const googleSignIn = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/portal";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const PortalLogin = () => {
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "login" : "register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : form;
      const { data } = await axios.post(`${API}/auth/${endpoint}`, payload, { withCredentials: true });
      setUser(data);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="portal-login">
      <h1 className="font-heading text-3xl font-bold text-white mb-2">Member Portal</h1>
      <p className="text-zinc-400 mb-8">Sign in to manage your membership, vehicles, and billing.</p>

      <div className="flex gap-2 mb-6">
        <button
          data-testid="portal-tab-login"
          onClick={() => setMode("login")}
          className={`flex-1 rounded-sm px-4 py-2.5 text-sm font-semibold transition-colors ${mode === "login" ? "bg-blue-600 text-white" : "border border-zinc-800 text-zinc-400 hover:text-white"}`}
        >
          Sign In
        </button>
        <button
          data-testid="portal-tab-register"
          onClick={() => setMode("register")}
          className={`flex-1 rounded-sm px-4 py-2.5 text-sm font-semibold transition-colors ${mode === "register" ? "bg-blue-600 text-white" : "border border-zinc-800 text-zinc-400 hover:text-white"}`}
        >
          Create Account
        </button>
      </div>

      <form onSubmit={submit} className="space-y-4 bg-[#121212] border border-zinc-800 rounded-xl p-6">
        {mode === "register" && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-zinc-300 text-sm">First Name</Label>
              <Input data-testid="portal-input-first-name" className={inputCls} required value={form.first_name} onChange={set("first_name")} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-300 text-sm">Last Name</Label>
              <Input data-testid="portal-input-last-name" className={inputCls} required value={form.last_name} onChange={set("last_name")} />
            </div>
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-zinc-300 text-sm">Email</Label>
          <Input data-testid="portal-input-email" type="email" className={inputCls} required value={form.email} onChange={set("email")} />
        </div>
        {mode === "register" && (
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-sm">Mobile Phone</Label>
            <Input data-testid="portal-input-phone" type="tel" className={inputCls} required value={form.phone} onChange={set("phone")} />
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-zinc-300 text-sm">Password{mode === "register" ? " (8+ characters)" : ""}</Label>
          <Input data-testid="portal-input-password" type="password" className={inputCls} required minLength={mode === "register" ? 8 : undefined} value={form.password} onChange={set("password")} />
        </div>
        <button
          data-testid="portal-submit-button"
          type="submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-sm px-6 py-3 font-semibold transition-colors"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? <LogIn className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
          {mode === "login" ? "Sign In" : "Create Account"}
        </button>
      </form>

      <div className="flex items-center gap-3 my-5">
        <div className="h-px bg-zinc-800 flex-1" />
        <span className="text-zinc-600 text-xs uppercase tracking-wider">or</span>
        <div className="h-px bg-zinc-800 flex-1" />
      </div>

      <button
        data-testid="portal-google-button"
        onClick={googleSignIn}
        className="w-full inline-flex items-center justify-center gap-2 border border-zinc-700 hover:border-zinc-500 text-white rounded-sm px-6 py-3 font-semibold transition-colors"
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
        Sign in with Google
      </button>

      <p className="text-zinc-500 text-sm text-center mt-8">
        Not a member yet?{" "}
        <Link to="/membership" className="text-blue-400 hover:text-blue-300 underline" data-testid="portal-join-link">
          Join for $39.99/month
        </Link>
      </p>
    </div>
  );
};
